import React, { useState } from 'react';
import {
  Plus,
  Maximize2,
  Minimize2,
  Clock,
  GitBranch,
  Mail,
  User,
  Check,
  ShoppingBag,
  AlertCircle,
} from 'lucide-react';
import { AutomationActionNodeSubType, AutomationNodeType } from '@frontend/app/types/globalTypes';
import { WorkletSpecUri } from '@frontend/app/containers/Projects/constants';
import { Button } from '@/shadcn/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { Badge } from '@/shadcn/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { NodeCategory, NodeTemplate } from '../types';

interface FloatingNodePaletteProps {
  programData?: {
    program?: {
      isFlexibleSpec?: boolean;
      workItems?: Array<{
        specURI?: string | null;
      }>;
    } | null;
  } | null;
  isStaOffer?: boolean;
}

export const FloatingNodePalette: React.FC<FloatingNodePaletteProps> = ({ programData, isStaOffer }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const delayNodes: NodeTemplate[] = [
    {
      type: AutomationNodeType.DELAY,
      category: NodeCategory.UTILITY,
      displayName: 'Wait',
      description: 'Wait for a specified amount of time',
      icon: 'clock',
      defaultMetadata: {}, // No default values - user must configure
      allowsChildren: true,
    },
  ];

  const conditionNodes: NodeTemplate[] = [
    {
      type: AutomationNodeType.CONDITION,
      category: NodeCategory.LOGIC,
      displayName: 'Condition',
      description: 'Check if a condition is true or false',
      icon: 'check',
      defaultMetadata: {},
      allowsChildren: true,
      maxChildren: 2,
    },
    {
      type: AutomationNodeType.JUMP,
      category: NodeCategory.LOGIC,
      displayName: 'Branch',
      description: 'Split into multiple parallel paths',
      icon: 'branch',
      defaultMetadata: { branchCount: 2 },
      allowsChildren: true,
    },
  ];

  // Check if project has product catalog stage
  const isFlexProject = programData?.program?.isFlexibleSpec || false;
  const doesProductCatalogStageExists =
    programData?.program?.workItems?.some(
      (workItem: any) => workItem.specURI === WorkletSpecUri.SendBrandProductCatalogWorkletSpecification,
    ) || false;
  // Don't disable product catalog action when isStaOffer is true
  const isProductCatalogActionDisabled = isStaOffer ? false : (!isFlexProject || !doesProductCatalogStageExists);

  const actionNodes: NodeTemplate[] = [
    {
      type: AutomationNodeType.ACTION,
      subType: AutomationActionNodeSubType.SEND_EMAIL,
      category: NodeCategory.ACTION,
      displayName: 'Send Email',
      description: 'Send an email to the member',
      icon: 'mail',
      defaultMetadata: {},
      allowsChildren: false,
    },
    {
      type: AutomationNodeType.ACTION,
      subType: AutomationActionNodeSubType.UPDATE_MEMBER_DATA,
      category: NodeCategory.ACTION,
      displayName: 'Update Member Details',
      description: 'Update member information',
      icon: 'user',
      defaultMetadata: {},
      allowsChildren: false,
    },
    {
      type: AutomationNodeType.ACTION,
      subType: AutomationActionNodeSubType.HANDLE_PFA_BOOST,
      category: NodeCategory.ACTION,
      displayName: 'Apply Boost',
      description: 'Apply a performance boost',
      icon: 'gift',
      defaultMetadata: {},
      allowsChildren: false,
    },
    {
      type: AutomationNodeType.ACTION,
      subType: AutomationActionNodeSubType.SEND_PRODUCT_CATALOG,
      category: NodeCategory.ACTION,
      displayName: 'Send Product Catalog',
      description: 'Send a product catalog to the member',
      icon: 'shopping-bag',
      defaultMetadata: {},
      allowsChildren: false,
      disabled: isProductCatalogActionDisabled,
    },
  ];

  const handleDragStart = (e: React.DragEvent, template: NodeTemplate) => {
    e.dataTransfer.setData('application/json', JSON.stringify(template));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const renderNodeItem = (template: NodeTemplate & { disabled?: boolean }) => {
    const isDisabled = template.disabled || false;

    const itemElement = (
      <div
        key={`${template.type}-${template.subType || 'default'}`}
        draggable={!isDisabled}
        onDragStart={(e) => (isDisabled ? e.preventDefault() : handleDragStart(e, template))}
        className={`flex items-center gap-3 p-3 rounded-lg border border-grey-2 transition-all ${
          isDisabled
            ? 'bg-grey-1 opacity-50 cursor-not-allowed'
            : 'bg-white hover:bg-grey-1 hover:border-blue-300 cursor-move'
        }`}
      >
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
          {template.icon === 'clock' && <Clock className="h-4 w-4 text-blue-600" />}
          {template.icon === 'check' && <Check className="h-4 w-4 text-purple-600" />}
          {template.icon === 'branch' && <GitBranch className="h-4 w-4 text-indigo-600" />}
          {template.icon === 'mail' && <Mail className="h-4 w-4 text-green-600" />}
          {template.icon === 'user' && <User className="h-4 w-4 text-orange-600" />}
          {template.icon === 'gift' && <Plus className="h-4 w-4 text-pink-600" />}
          {template.icon === 'shopping-bag' && <ShoppingBag className="h-4 w-4 text-orange-600" />}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-grey-6">{template.displayName}</h4>
            {isDisabled && <AlertCircle className="h-3 w-3 text-yellow-500" />}
          </div>
          <p className="text-xs text-grey-5">{template.description}</p>
        </div>
      </div>
    );

    // Wrap disabled Send Product Catalog with tooltip
    if (!isStaOffer && template.subType === AutomationActionNodeSubType.SEND_PRODUCT_CATALOG && isDisabled) {
      return (
        <TooltipProvider key={`${template.type}-${template.subType || 'default'}`}>
          <Tooltip>
            <TooltipTrigger asChild>{itemElement}</TooltipTrigger>
            <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium">
              <p className="text-xs">For this action, project must have product catalog stage</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return itemElement;
  };

  if (!isOpen) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => setIsOpen(true)}
              className="fixed left-6 bottom-6 w-12 h-12 rounded-full bg-blue-500 hover:bg-blue-600 shadow-lg z-50"
            >
              <Plus className="h-6 w-6 text-white" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Add a block</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Card
      className={`fixed left-6 bottom-6 z-50 shadow-xl border-grey-2 transition-all duration-300 ${
        isMaximized ? 'w-80 h-[calc(100vh-120px)] bottom-6' : 'w-80 max-h-96 bottom-6'
      }`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Add Block</CardTitle>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setIsMaximized(!isMaximized)}>
              {isMaximized ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setIsOpen(false)}>
              ×
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className={`space-y-4 ${isMaximized ? 'overflow-y-auto' : ''}`}>
        {/* Delays Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              Delays
            </Badge>
          </div>
          <div className="space-y-2">{delayNodes.map(renderNodeItem)}</div>
        </div>

        {/* Conditions Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              Conditions
            </Badge>
          </div>
          <div className="space-y-2">{conditionNodes.map(renderNodeItem)}</div>
        </div>

        {/* Actions Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              Actions
            </Badge>
          </div>
          <div className={`space-y-2 ${isMaximized ? 'max-h-60 overflow-y-auto' : ''}`}>
            {actionNodes.map(renderNodeItem)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
